import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import Select from "react-select";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
    Row, Col, Card, CardBody, Button, Input, Table,
    Nav, NavItem, NavLink, TabContent, TabPane, Badge, Modal, ModalHeader, ModalBody
} from "reactstrap";
import classnames from "classnames";
import { getCookie } from "../../Helpers/Utils";

const DispatchExecution = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState("expenses");
    const [dispatchData, setDispatchData] = useState({
        dispatch: {},
        customer: {},
        assigned_vehicles: [],
        expenses: [],
        salaries: [],
        documents: []
    });

    const [previewImage, setPreviewImage] = useState(null);

    // Form States
    const [expenseForm, setExpenseForm] = useState({
        expense_type: { value: "fuel", label: "Fuel" },
        amount: "",
        expense_date: dayjs(),
        remarks: ""
    });

    // Salary Form with Driver Selection
    const [salaryForm, setSalaryForm] = useState({
        driver_id: null, // React-select object
        salary_amount: "",
        salary_date: dayjs(),
        remarks: ""
    });

    const [selectedFiles, setSelectedFiles] = useState([]);

    const expenseOptions = [
        { value: "fuel", label: "Fuel" },
        { value: "parking", label: "Parking" },
        { value: "cash_card", label: "CashCard / ERP" },
        { value: "service", label: "Service" },
        { value: "other", label: "Other" }
    ];

    // Convert assigned_vehicles to Select Options
    const driverOptions = dispatchData.assigned_vehicles?.map(v => ({
        value: v.driver_id,
        label: `${v.driver_name} (${v.vehicle_number} - ${v.lorry_type})`
    })) || [];

    const loadData = useCallback(async () => {
        dispatch(loadingTrue());
        try {
            const res = await Handler({ method: "get", url: `/dispatch/view.php?id=${id}` });
            if (res?.success && res.data) {
                setDispatchData(res.data);
            } else {
                toast.error(res?.message || "Data not found");
            }
        } catch (err) {
            toast.error("Backend connection failed!");
        }
        dispatch(loadingFalse());
    }, [id, dispatch]);

    useEffect(() => { loadData(); }, [loadData]);

    /* ================= HANDLERS ================= */
    const handleAddExpense = async (e) => {
        e.preventDefault();
        dispatch(loadingTrue());
        const payload = {
            action: "add_expense",
            dispatch_id: id,
            expense_type: expenseForm.expense_type.value,
            amount: expenseForm.amount,
            expense_date: expenseForm.expense_date.format("YYYY-MM-DD"),
            remarks: expenseForm.remarks
        };
        const res = await Handler({ method: "post", url: "/dispatch/expense.php", data: payload });
        dispatch(loadingFalse());
        if (res?.success) {
            toast.success("Expense Added");
            setExpenseForm({ ...expenseForm, amount: "", remarks: "" });
            loadData();
        } else toast.error(res?.message);
    };

    const handleAddSalary = async (e) => {
        e.preventDefault();
        if (!salaryForm.driver_id) return toast.warning("Please select a driver");

        dispatch(loadingTrue());
        const payload = {
            action: "add_driver_salary",
            dispatch_id: id,
            driver_id: salaryForm.driver_id.value, // Sending selected driver ID
            salary_amount: salaryForm.salary_amount,
            salary_date: salaryForm.salary_date.format("YYYY-MM-DD"),
            remarks: salaryForm.remarks
        };
        const res = await Handler({ method: "post", url: "/dispatch/expense.php", data: payload });
        dispatch(loadingFalse());
        if (res?.success) {
            toast.success("Driver Salary Recorded");
            setSalaryForm({ ...salaryForm, driver_id: null, salary_amount: "", remarks: "" });
            loadData();
        } else toast.error(res?.message);
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = files.map(file => ({ file, preview: URL.createObjectURL(file) }));
        setSelectedFiles([...selectedFiles, ...validFiles]);
    };

    const handleUploadDocs = async () => {
        if (selectedFiles.length === 0) return toast.warning("Select files first");

        const formData = new FormData();
        formData.append("action", "add_documents");
        formData.append("dispatch_id", id);

        // Mukkiyamaana step: Array looping correct-ah irukanum
        selectedFiles.forEach(f => {
            formData.append("documents[]", f.file);
        });

        dispatch(loadingTrue());

        try {
            const token = getCookie("token"); // Unga auth token yeduthukonga
            // const response = await fetch(`http://localhost/vehicle_pos/dispatch/document.php`, {
            const response = await fetch(`https://sfs.software.dizoratbd.com/vehicle_pos/dispatch/document.php`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`
                    // Note: "Content-Type" header-a manual-ah set panna koodathu (browser automatic-ah boundary add pannum)
                },
                body: formData
            });
            const res = await response.json();
            dispatch(loadingFalse());

            if (res?.status) {
                toast.success("Documents Uploaded Successfully");
                setSelectedFiles([]);
                loadData();
            } else {
                toast.error(res?.message || "Upload Failed");
            }
        } catch (error) {
            dispatch(loadingFalse());
            toast.error("Network error during upload");
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="execution-page p-3 bg-light min-vh-100">
                {/* Header Section */}
                <div className="d-flex justify-content-between align-items-center mb-4 bg-white p-3 rounded-4 shadow-sm border">
                    <div>
                        <h4 className="fw-bold text-dark mb-1">Execution Mode: {dispatchData.dispatch?.dispatch_number}</h4>
                        <p className="text-muted small mb-0 text-uppercase letter-spacing-1">
                            Vessel: {dispatchData.dispatch?.vessel_name} | Pallets: {dispatchData.dispatch?.pallet_count}
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <Badge color="primary" className="px-3 py-2 rounded-pill shadow-sm">
                            {dispatchData.dispatch?.status?.toUpperCase()}
                        </Badge>
                        <Button color="secondary" outline size="sm" className="rounded-pill px-3" onClick={() => navigate(-1)}>Back</Button>
                    </div>
                </div>

                <Row>
                    <Col lg={12}>
                        <Nav tabs className="border-0 mb-4 bg-white p-1 rounded-pill shadow-sm border" style={{ width: 'fit-content' }}>
                            {['expenses', 'salary', 'documents'].map(tab => (
                                <NavItem key={tab}>
                                    <NavLink
                                        className={classnames("border-0 rounded-pill px-4 fw-bold cursor-pointer", { active: activeTab === tab })}
                                        onClick={() => setActiveTab(tab)}
                                        style={{ cursor: 'pointer', transition: '0.3s' }}
                                    >
                                        {tab.toUpperCase()}
                                    </NavLink>
                                </NavItem>
                            ))}
                        </Nav>
                    </Col>

                    <Col lg={12}>
                        <TabContent activeTab={activeTab}>
                            {/* --- EXPENSES TAB --- */}
                            <TabPane tabId="expenses">
                                <Row>
                                    <Col md={4}>
                                        <Card className="border-0 shadow-sm rounded-4 mb-3">
                                            <CardBody>
                                                <h6 className="fw-bold mb-3 text-primary">Add New Expense</h6>
                                                <form onSubmit={handleAddExpense}>
                                                    <div className="mb-3">
                                                        <label className="extra-small fw-bold">Expense Type</label>
                                                        <Select options={expenseOptions} value={expenseForm.expense_type} onChange={val => setExpenseForm({ ...expenseForm, expense_type: val })} />
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="extra-small fw-bold">Amount ($)</label>
                                                        <Input type="number" step="0.01" required value={expenseForm.amount} onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })} />
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="extra-small fw-bold">Date</label>
                                                        <DatePicker value={expenseForm.expense_date} onChange={val => setExpenseForm({ ...expenseForm, expense_date: val })} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="extra-small fw-bold">Remarks</label>
                                                        <Input type="textarea" rows="2" value={expenseForm.remarks} onChange={e => setExpenseForm({ ...expenseForm, remarks: e.target.value })} />
                                                    </div>
                                                    <Button color="primary" block className="rounded-3 py-2 fw-bold">Save Expense</Button>
                                                </form>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md={8}>
                                        <Card className="border-0 shadow-sm rounded-4 overflow-hidden border">
                                            <Table responsive hover className="mb-0 align-middle">
                                                <thead className="bg-light"><tr><th>Type</th><th>Date</th><th>Amount</th><th>Remarks</th><th>Action</th></tr></thead>
                                                <tbody>
                                                    {dispatchData.expenses?.length > 0 ? dispatchData.expenses.map(ex => (
                                                        <tr key={ex.id}>
                                                            <td><Badge color="soft-info" className="text-info bg-info bg-opacity-10 px-3 py-2">{ex.expense_type}</Badge></td>
                                                            <td>{dayjs(ex.expense_date).format("DD-MM-YYYY")}</td>
                                                            <td className="text-danger fw-bold">-${ex.amount}</td>
                                                            <td className="small text-muted">{ex.remarks || '-'}</td>
                                                            <td><i className="bi bi-trash text-danger cursor-pointer"></i></td>
                                                        </tr>
                                                    )) : <tr><td colSpan="5" className="text-center py-5 text-muted">No expenses found</td></tr>}
                                                </tbody>
                                            </Table>
                                        </Card>
                                    </Col>
                                </Row>
                            </TabPane>

                            {/* --- SALARY TAB (UPDATED WITH DRIVER SELECTION) --- */}
                            <TabPane tabId="salary">
                                <Row>
                                    <Col md={4}>
                                        <Card className="border-0 shadow-sm rounded-4 mb-3">
                                            <CardBody>
                                                <h6 className="fw-bold mb-3 text-success">Driver Salary Record</h6>
                                                <form onSubmit={handleAddSalary}>
                                                    <div className="mb-3">
                                                        <label className="extra-small fw-bold text-uppercase">Select Driver</label>
                                                        <Select
                                                            options={driverOptions}
                                                            value={salaryForm.driver_id}
                                                            placeholder="Search Driver Name..."
                                                            onChange={val => setSalaryForm({ ...salaryForm, driver_id: val })}
                                                        />
                                                        <p className="extra-small text-muted mt-1">*Only drivers assigned to this dispatch are listed.</p>
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="extra-small fw-bold">Salary Amount ($)</label>
                                                        <Input type="number" step="0.01" required value={salaryForm.salary_amount} onChange={e => setSalaryForm({ ...salaryForm, salary_amount: e.target.value })} />
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="extra-small fw-bold">Date</label>
                                                        <DatePicker value={salaryForm.salary_date} onChange={val => setSalaryForm({ ...salaryForm, salary_date: val })} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                                                    </div>
                                                    <div className="mb-3">
                                                        <label className="extra-small fw-bold">Remarks</label>
                                                        <Input type="textarea" rows="2" value={salaryForm.remarks} onChange={e => setSalaryForm({ ...salaryForm, remarks: e.target.value })} />
                                                    </div>
                                                    <Button color="success" block className="rounded-3 py-2 fw-bold text-white">Save Salary</Button>
                                                </form>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                    <Col md={8}>
                                        <Card className="border-0 shadow-sm rounded-4 overflow-hidden border">
                                            <Table responsive hover className="mb-0 align-middle">
                                                <thead className="bg-light"><tr><th>Driver Name</th><th>Date</th><th>Amount</th><th>Remarks</th><th>Action</th></tr></thead>
                                                <tbody>
                                                    {dispatchData.salaries?.length > 0 ? dispatchData.salaries.map(s => (
                                                        <tr key={s.id}>
                                                            <td className="fw-bold">{s.driver_name || 'N/A'}</td>
                                                            <td>{dayjs(s.salary_date).format("DD-MM-YYYY")}</td>
                                                            <td className="text-success fw-bold">${parseFloat(s.salary_amount).toFixed(2)}</td>
                                                            <td className="small text-muted">{s.remarks || '-'}</td>
                                                            <td><i className="bi bi-trash text-danger cursor-pointer"></i></td>
                                                        </tr>
                                                    )) : <tr><td colSpan="5" className="text-center py-5 text-muted">No salary records found</td></tr>}
                                                </tbody>
                                            </Table>
                                        </Card>
                                    </Col>
                                </Row>
                            </TabPane>

                            {/* --- DOCUMENTS TAB --- */}
                            <TabPane tabId="documents">
                                <Card className="border-0 shadow-sm rounded-4 border">
                                    <CardBody>
                                        <div className="d-flex justify-content-between align-items-center mb-4">
                                            <h6 className="fw-bold mb-0">Documents & POD Images</h6>
                                            <div className="d-flex gap-2">
                                                <Input type="file" multiple id="fileInput" className="d-none" onChange={handleFileChange} accept="image/*" />
                                                <Button color="dark" outline className="rounded-pill px-4" size="sm" onClick={() => document.getElementById('fileInput').click()}>+ Select Images</Button>
                                                <Button color="success" className="rounded-pill px-4 shadow-sm" size="sm" onClick={handleUploadDocs}>Upload Files</Button>
                                            </div>
                                        </div>

                                        {selectedFiles.length > 0 && (
                                            <div className="d-flex gap-3 flex-wrap mb-4 p-3 bg-light rounded-4 border-dashed">
                                                {selectedFiles.map((file, idx) => (
                                                    <div key={idx} className="position-relative">
                                                        <img src={file.preview} className="rounded-3 border shadow-sm" style={{ width: '100px', height: '100px', objectFit: 'cover' }} alt="p" />
                                                        <Badge color="danger" className="position-absolute top-0 end-0 cursor-pointer rounded-circle" style={{ transform: 'translate(40%, -40%)' }} onClick={() => setSelectedFiles(selectedFiles.filter((_, i) => i !== idx))}>×</Badge>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <Row className="g-3">
                                            {dispatchData.documents?.length > 0 ? dispatchData.documents.map(doc => (
                                                <Col md={3} lg={2} key={doc.id}>
                                                    <div className="position-relative group overflow-hidden rounded-4 border shadow-sm">
                                                        <img
                                                            // src={`http://localhost/vehicle_pos/uploads/dispatch/${id}/${doc.file_name}`}
                                                            src={`https://sfs.software.dizoratbd.com/vehicle_pos/uploads/dispatch/${id}/${doc.file_name}`}
                                                            className="img-fluid cursor-pointer"
                                                            style={{ height: '150px', width: '100%', objectFit: 'cover' }}
                                                            // onClick={() => setPreviewImage(`http://localhost/vehicle_pos/uploads/dispatch/${id}/${doc.file_name}`)}
                                                            onClick={() => setPreviewImage(`https://sfs.software.dizoratbd.com/vehicle_pos/uploads/dispatch/${id}/${doc.file_name}`)}
                                                            alt="pod"
                                                        />
                                                        <div className="p-2 bg-white text-truncate small text-center fw-bold">{doc.file_name}</div>
                                                    </div>
                                                </Col>
                                            )) : <div className="text-center py-5 w-100 text-muted">No documents uploaded.</div>}
                                        </Row>
                                    </CardBody>
                                </Card>
                            </TabPane>
                        </TabContent>
                    </Col>
                </Row>

                <Modal isOpen={!!previewImage} toggle={() => setPreviewImage(null)} size="lg" centered>
                    <ModalHeader toggle={() => setPreviewImage(null)} className="border-0 pb-0">Document Preview</ModalHeader>
                    <ModalBody className="text-center p-4">
                        <img src={previewImage} alt="Large POD" className="img-fluid rounded-4 shadow-lg" />
                    </ModalBody>
                </Modal>
            </div>
        </LocalizationProvider>
    );
};

export default DispatchExecution;